// Boiler Plate Imports
import React, { useEffect, useMemo, useState } from "react";
import colors from "../../theme/colors";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import {
  remove as removeNotification,
  add as addNotification,
} from "../../redux-store/notificationsSlice";
import { update as setBreadCrumbs } from "../../redux-store/breadCrumbsSlice";
import API from "../../api";

// Conatiner Component Imports
import { Card } from "@mui/material";
import BreadCrumbs from "../../components/Navigation/breadcrumbs";
import TableHeader from "../../components/Page/tableHeader";

// Page Component Imports
import { FormatPhoneNumber } from "../../components/PhoneNumberInput";
import MetricBoxes from "../../components/Page/metricBoxes";
import {
  TableComponent,
  ItemLink,
  StatusTag,
} from "../../components/Table/dataGridPro";
import TableSubHeader from "../../components/Page/tableSubHeader";

const PaddingWrapper = styled.div`
  padding: 0 15px 10px;
  @media (max-width: 1080px) {
    padding: 0 0px 10px;
  }
`;

const Table = React.memo(TableComponent);

const AdminAlarmsContainer = () => {
  const [reloadData, setReloadData] = useState(false);
  const [metrics, setMetrics] = useState([]);

  const dispatch = useDispatch();

  const tableColumns = useMemo(() => {
    return [
      {
        headerName: "Alarm Type",
        field: "alarmInfoDisplayName",
        valueGetter: (value, row) => {
          return row.alarmInfoDisplayName;
        },
        renderCell: ({ row }) => {
          return (
            <ItemLink to={`/${row.organizationId}/alarms/${row.id}`}>
              {row.alarmInfoDisplayName}
            </ItemLink>
          );
        },
      },
      {
        headerName: "Alarm Status",
        field: "active",
        display: "flex",
        valueGetter: (value, row) => {
          return row.active;
        },
        renderCell: ({ row }) => {
          const statusString = row.active === "true" ? "Active" : "Resolved";
          return (
            <StatusTag className={row.active === "true" ? "alarm" : "resolved"}>
              {statusString}
            </StatusTag>
          );
        },
      },
      {
        headerName: "Resolution Status",
        field: "resolutionStatus",
        display: "flex",
        valueGetter: (value, row) => {
          return row.resolutionStatus;
        },
        renderCell: ({ row }) => {
          let statusString = "Not Started";
          let className = "alarm";
          if (row.resolutionStatus === "resolved") {
            statusString = "Resolved";
            className = "resolved";
          } else if (row.resolutionStatus === "progress") {
            statusString = "In Progress";
            className = "progress";
          }
          return <StatusTag className={className}>{statusString}</StatusTag>;
        },
      },
      {
        headerName: "Tank Name",
        field: "tankName",
        valueGetter: (value, row) => {
          return row.tankName;
        },
        renderCell: ({ row }) => {
          return (
            <ItemLink to={`/${row.organizationId}/tanks/${row.tankId}`}>
              {row.tankName}
            </ItemLink>
          );
        },
      },
      {
        headerName: "Tank Organization",
        field: "organizationName",
        valueGetter: (value, row) => {
          return row.organizationName;
        },
        renderCell: ({ row }) => {
          return (
            <ItemLink to={`/${row.organizationId}/organization`}>
              {row.organizationName}
            </ItemLink>
          );
        },
      },
      {
        headerName: "Tank Location",
        field: "locationName",
        valueGetter: (value, row) => {
          return row.locationName;
        },
        renderCell: ({ row }) => {
          return (
            <ItemLink to={`/${row.organizationId}/locations/${row.locationId}`}>
              {row.locationName}
            </ItemLink>
          );
        },
      },
      {
        headerName: "Alarm Created",
        field: "created",
        type: "dateTime",
        valueGetter: (value, row) => {
          return new Date(row.created);
        },
      },
      {
        headerName: "Alarm Resolved",
        field: "resolved",
        valueGetter: (value, row) => {
          return row.resolvedDate;
        },
        renderCell: ({ row }) => {
          if (row.resolvedDate) {
            return new Date(row.resolvedDate).toLocaleString();
          } else {
            return "-";
          }
        },
      },
    ];
  }, []);

  useEffect(() => {
    dispatch(
      setBreadCrumbs([
        {
          title: "Admin Alarms",
          link: "/admin/alarms",
        },
      ]),
    );
    setMetrics([
      {
        title: "LOW WEIGHT",
        value: "--",
      },
      {
        title: "HIGH CONSUMPTION RATE",
        value: "--",
      },
      {
        title: "LOW EXT. TEMP",
        value: "--",
      },
      {
        title: "LOW BATTERY",
        value: "--",
      },
      {
        title: "OFFLINE",
        value: "--",
      },
    ]);
  }, []);

  const dataLayer = {
    GetAllAlarms: async () => {
      const { alarms = [], metrics = {} } = await API.Alarm.AlarmsGetAll();

      setMetrics([
        {
          title: "LOW WEIGHT",
          value: metrics?.lowWeightCount,
        },
        {
          title: "HIGH CONSUMPTION RATE",
          value: metrics?.highConsumptionRateCount,
        },
        {
          title: "LOW EXT. TEMP",
          value: metrics?.lowExtTempCount,
        },
        {
          title: "LOW BATTERY",
          value: metrics?.lowBatteryCount,
        },
        {
          title: "OFFLINE",
          value: metrics?.offlineCount,
        },
      ]);

      alarms.sort((a, b) => {
        return b.name - a.name;
      });

      return alarms;
    },
  };

  return (
    <>
      <MetricBoxes title={"Alarm Totals"} metrics={metrics} />
      <PaddingWrapper>
        <Card>
          <TableHeader>
            <div>
              <h2>Alarms</h2>
              <TableSubHeader>
                View all Alarms in the Boreas system from the last 30 days
              </TableSubHeader>
            </div>
          </TableHeader>

          <Table
            columns={tableColumns}
            label={""}
            isAdminView={true}
            reloadData={reloadData}
            placeholder="There are no Alarms yet"
            getRowsFunction={dataLayer.GetAllAlarms}
            initialState={{
              pinnedColumns: { left: ["name"] },
              sorting: {
                sortModel: [
                  { field: "activeAlarmsCount", sort: "desc" },
                  { field: "name", sort: "asc" },
                ],
              },
            }}
          />
        </Card>
      </PaddingWrapper>
    </>
  );
};

export default AdminAlarmsContainer;
